import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FormField, FormFieldLabel } from 'styles/common.styles';
import { theme, cssVar } from 'styles/theme';

const DEFAULT_MIN = 1990;
const DEFAULT_MAX = 2100;

/** 현재 달력 기준 회계연도 문자열 (YYYY) */
export function getCurrentFiscalYear() {
  return String(new Date().getFullYear());
}

const Wrap = styled.div`
  display: inline-flex;
  align-items: stretch;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${(p) =>
    p.$readonly
      ? cssVar('color-bg-medium', theme.colors.background.medium)
      : cssVar('color-bg-white', theme.colors.white)};
  overflow: hidden;
  width: ${(p) => p.$width || '88px'};
  height: 28px;
  box-sizing: border-box;

  &:focus-within {
    border-color: ${(p) =>
      p.$readonly ? cssVar('color-border', theme.colors.border) : cssVar('color-primary', theme.colors.primary)};
    box-shadow: ${(p) => (p.$readonly ? 'none' : '0 0 0 2px var(--erp-color-focus-ring, rgba(37, 99, 235, 0.15))')};
  }
`;

const YearInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0 6px;
  font-size: ${theme.typography.fontSize.sm};
  color: ${cssVar('color-text', theme.colors.text.primary)};
  text-align: center;
  font-variant-numeric: tabular-nums;
  cursor: ${(p) => (p.readOnly ? 'default' : 'text')};

  &:disabled {
    cursor: not-allowed;
  }

  appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Spinner = styled.div`
  display: flex;
  flex-direction: column;
  width: 18px;
  flex-shrink: 0;
  border-left: 1px solid ${cssVar('color-border', theme.colors.border)};
  background: ${cssVar('color-bg-light', theme.colors.secondary)};
`;

const SpinBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  line-height: 1;
  opacity: ${(p) => (p.disabled ? 0.4 : 1)};

  &:hover:not(:disabled) {
    background: ${cssVar('color-primary-light', theme.colors.primaryLight)};
    color: ${cssVar('color-primary', theme.colors.primary)};
  }

  &:active:not(:disabled) {
    background: ${cssVar('color-primary', theme.colors.primary)};
    color: var(--erp-color-on-accent, #FFFFFF);
  }

  svg {
    display: block;
  }
`;

function parseYear(value) {
  const n = Number(String(value ?? '').replace(/[^\d]/g, '').slice(0, 4));
  return Number.isFinite(n) ? n : NaN;
}

function clampYear(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

/**
 * 회계연도 검색조건 공통 필드
 * - 우측 ▲/▼ 클릭으로 연도 ±1
 * - 휠로 연도 ±1 (필드 위)
 * - FormArea `type: 'fiscalYear'` 로 사용
 */
const FiscalYearField = ({
  name,
  label,
  value = '',
  width,
  flex,
  labelWidth,
  fieldWidth,
  showColon = false,
  readonly = false,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  onChange,
  onKeyDown,
  onKeyUp,
  fieldProps = {},
}) => {
  const wrapRef = useRef(null);
  const yearNum = clampYear(parseYear(value) || Number(getCurrentFiscalYear()), min, max);
  const display = String(value ?? '').trim() !== '' ? String(value).slice(0, 4) : String(yearNum);

  const emit = useCallback(
    (nextYear) => {
      if (readonly || !onChange) return;
      const y = clampYear(nextYear, min, max);
      onChange({
        target: {
          name,
          value: String(y),
        },
      });
    },
    [readonly, onChange, name, min, max]
  );

  const stepBy = useCallback(
    (delta) => {
      const base = parseYear(display);
      const cur = Number.isFinite(base) ? base : Number(getCurrentFiscalYear());
      emit(cur + delta);
    },
    [display, emit]
  );

  const stepByRef = useRef(stepBy);
  stepByRef.current = stepBy;
  const readonlyRef = useRef(readonly);
  readonlyRef.current = readonly;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (readonlyRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      stepByRef.current(e.deltaY < 0 ? 1 : -1);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handleInputChange = (e) => {
    if (readonly) return;
    const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
    onChange?.({
      target: { name, value: raw },
    });
  };

  const handleBlur = () => {
    if (readonly) return;
    const n = parseYear(display);
    if (!Number.isFinite(n) || String(display).length < 4) {
      emit(yearNum);
      return;
    }
    emit(n);
  };

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
    if (readonly) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      stepBy(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      stepBy(-1);
    }
  };

  return (
    <FormField flex={flex || '0 0 auto'} width={fieldWidth || 'auto'}>
      {label && (
        <FormFieldLabel width={labelWidth}>{showColon ? `${label}:` : label}</FormFieldLabel>
      )}
      <Wrap ref={wrapRef} $width={width || '88px'} $readonly={readonly}>
        <YearInput
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={display}
          readOnly={readonly}
          disabled={readonly}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={onKeyUp}
          aria-label={label || '회계연도'}
          {...fieldProps}
        />
        <Spinner>
          <SpinBtn
            type="button"
            tabIndex={-1}
            disabled={readonly || yearNum >= max}
            aria-label="다음 연도"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => stepBy(1)}
          >
            <svg width="8" height="6" viewBox="0 0 8 6" aria-hidden>
              <path d="M4 0.5L7.5 5.5H0.5L4 0.5Z" fill="currentColor" />
            </svg>
          </SpinBtn>
          <SpinBtn
            type="button"
            tabIndex={-1}
            disabled={readonly || yearNum <= min}
            aria-label="이전 연도"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => stepBy(-1)}
          >
            <svg width="8" height="6" viewBox="0 0 8 6" aria-hidden>
              <path d="M4 5.5L0.5 0.5H7.5L4 5.5Z" fill="currentColor" />
            </svg>
          </SpinBtn>
        </Spinner>
      </Wrap>
    </FormField>
  );
};

export default FiscalYearField;
